import React, { useEffect, useState, useRef, useCallback } from "react";

import { makeStyles } from "tss-react/mui";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Fade,
  ListItemText,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  Card,
  CardContent,
} from "@mui/material";
import queryString from "query-string";
import cloneDeep from "lodash.clonedeep";
import DeleteIcon from "@mui/icons-material/Delete";
import deepEqual from "deep-equal";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import AddIcon from "@mui/icons-material/Add";
import { v4 as uuid } from "uuid";
import Typography from "@mui/material/Typography";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import CategoryIcon from "@mui/icons-material/Category";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TitleBar from "../nav/title-bar";
import { useShoppingListStore } from "../../context-providers/shopping-list-store-provider";
import IntentButton, { Intent } from "../input/intent-button";
import ROUTES from "../../routes/routes";
import { DEFAULT_FADE_ANIMATION } from "../../constants";
import ConfirmDeleteDialog from "../dialog/confirm-delete-dialog";
import ShoppingListItem from "./shopping-list-item";
import usePreventReload from "../../hooks/use-prevent-reload";
import { useDragReorder } from "../../hooks/use-drag-reorder";
import { useAutoScroll } from "../../hooks/useAutoScroll";
import {
  formatShoppingListForAI,
  parseCategorizedTextToItems,
  groupItemsByCategory,
} from "../../util/shopping-list-ai-helper";
import { categorizeShoppingList } from "../../services/gemini-service";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
  },

  buttons: {
    display: "flex",
    flexDirection: "row",
    pointerEvents: "none",
    flexWrap: "wrap",
    justifyContent: "space-evenly",
    position: "sticky",
    zIndex: 10,
    [theme.breakpoints.down("sm")]: {
      top: "54px",
    },
    [theme.breakpoints.up("sm")]: {
      top: "64px",
    },
  },

  button: {
    pointerEvents: "all",
    margin: "5px 0",
  },

  noItems: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    color: "#999999",
  },

  list: {
    width: "100%",
  },

  addIcon: {
    padding: "9px",
    color: theme.palette.primary.main,
  },

  addText: {
    color: theme.palette.primary.main,
  },

  checkedItemsText: {
    color: theme.palette.grey.A400,
    padding: "10px 30px",
  },

  exit: {
    maxHeight: "60px",
    overflow: "hidden",
  },

  exitActive: {
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    transition: "all 500ms",
    overflow: "hidden",
  },

  enter: {
    maxHeight: 0,
    transition: "none",
    paddingTop: 0,
    paddingBottom: 0,
    overflow: "hidden",
  },

  enterActive: {
    maxHeight: "60px",
    paddingTop: "8px",
    paddingBottom: "8px",
    transition: "all 500ms",
    overflow: "hidden",
  },

  categoryHeader: {
    padding: "10px 16px",
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    userSelect: "none",
  },

  categoryTitle: {
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  dragGhost: {
    position: "fixed",
    pointerEvents: "none",
    zIndex: 9999,
    opacity: 0.8,
    transform: "rotate(-2deg) scale(0.95)",
    boxShadow: theme.shadows[8],
  },
}));

export default function ShoppingList() {
  const { classes } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    getUserList,
    getHomeList,
    saveUserList,
    saveHomeList,
    removeUserList,
    removeHomeList,
  } = useShoppingListStore();
  const { shoppingListId } = useParams();
  const [carefullyCancel, setCarefullyCancel] = useState(false);

  const query = queryString.parse(location.search);
  const editMode = query.edit === "true";
  const isHome = query.homeList === "true";

  const getShoppingList = isHome ? getHomeList : getUserList;
  const saveShoppingList = isHome ? saveHomeList : saveUserList;
  const removeShoppingList = isHome ? removeHomeList : removeUserList;

  const shoppingList = getShoppingList(shoppingListId);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingShoppingList, setPendingShoppingList] = useState(shoppingList);
  const [organizeMenuAnchor, setOrganizeMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [organizing, setOrganizing] = useState(false);
  const [organizeError, setOrganizeError] = useState("");
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set()
  );
  const [previewItems, setPreviewItems] = useState<any[] | null>(null);
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  // Drag and drop - create a ref that always returns document.documentElement
  const scrollContainerRef = useRef<HTMLElement>(null);

  // Update pendingShoppingList when shoppingList loads
  useEffect(() => {
    if (shoppingList && !pendingShoppingList) {
      setPendingShoppingList(shoppingList);
    }
  }, [shoppingList, pendingShoppingList]);

  // Set the ref to document.documentElement after mount
  useEffect(() => {
    scrollContainerRef.current = document.documentElement;
  }, []);

  const changesMade = !deepEqual(shoppingList, pendingShoppingList);

  usePreventReload(changesMade);

  useEffect(() => {
    if (carefullyCancel) {
      setCarefullyCancel(false);
      navigate(`${location.pathname}?homeList=${isHome ? "true" : "false"}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carefullyCancel]);

  useEffect(() => {
    if (!editMode && changesMade) {
      setPendingShoppingList(getShoppingList(shoppingListId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, shoppingList]);

  // Handle reordering of shopping list items via drag and drop
  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex || !pendingShoppingList) return;

      // Get unchecked and checked items
      const uncheckedItems = pendingShoppingList.items.filter(
        (item: any) => !item.checked
      );
      const checkedItems = pendingShoppingList.items.filter(
        (item: any) => item.checked
      );

      // Find the target item we're dropping on/near (in ORIGINAL position before the move)
      const targetItem = uncheckedItems[toIndex];

      // The dropped item takes the category of the item at the drop position
      const targetCategory = targetItem?.category;

      // Reorder the unchecked items
      const reordered = [...uncheckedItems];
      const [movedItem] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, movedItem);

      // Update the moved item's category
      const finalReordered = reordered.map((item, idx) => {
        if (idx === toIndex) {
          return { ...item, category: targetCategory };
        }
        return item;
      });

      setPendingShoppingList({
        ...pendingShoppingList,
        items: [...finalReordered, ...checkedItems],
      });
    },
    [pendingShoppingList]
  );

  // Drag and drop hooks
  const {
    draggedIndex,
    dragOverIndex,
    ghostPosition,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useDragReorder(handleReorder);

  const { updateCursorPosition, startAutoScroll, stopAutoScroll } =
    useAutoScroll({
      scrollContainerRef,
      axis: "vertical",
      zoneSize: 100,
      maxSpeed: 10,
    });

  // Wrap drag handlers with auto-scroll
  const handleDragStartWithScroll = useCallback(
    (index: number) => (e: React.DragEvent) => {
      handleDragStart(index)(e);
      startAutoScroll();
    },
    [handleDragStart, startAutoScroll]
  );

  const handleDragEndWithScroll = useCallback(() => {
    handleDragEnd();
    stopAutoScroll();
  }, [handleDragEnd, stopAutoScroll]);

  const handleTouchStartWithScroll = useCallback(
    (index: number) => (e: React.TouchEvent) => {
      handleTouchStart(index)(e);
      startAutoScroll();
      updateCursorPosition(e.touches[0].clientX, e.touches[0].clientY);
    },
    [handleTouchStart, startAutoScroll, updateCursorPosition]
  );

  // Global touch listeners for mobile drag
  useEffect(() => {
    if (draggedIndex !== null && ghostPosition) {
      const handleMove = (e: TouchEvent) => {
        e.preventDefault(); // Prevent page scrolling during drag
        handleTouchMove(e); // Update ghost position in the hook
        updateCursorPosition(e.touches[0].clientX, e.touches[0].clientY); // Update auto-scroll
      };

      const handleEnd = (e: TouchEvent) => {
        e.preventDefault();
        handleTouchEnd(e);
        stopAutoScroll();
      };

      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleEnd, { passive: false });
      return () => {
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleEnd);
      };
    }
  }, [
    draggedIndex,
    ghostPosition,
    updateCursorPosition,
    handleTouchMove,
    handleTouchEnd,
    stopAutoScroll,
  ]);

  // Show loading state while data is being fetched
  if (!shoppingList || !pendingShoppingList) {
    return (
      <Box className={classes.root}>
        <TitleBar title="Loading..." onBack={() => navigate("/shopping")} />
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  function onItemsChange(items: any) {
    setPendingShoppingList({
      ...pendingShoppingList,
      items,
    });
  }

  function addItem() {
    setPendingShoppingList({
      ...pendingShoppingList,
      items: [...pendingShoppingList.items, { id: uuid(), text: "" }],
    });
  }

  function addItemToCategory(categoryName: string | undefined) {
    setPendingShoppingList({
      ...pendingShoppingList,
      items: [
        ...pendingShoppingList.items,
        { id: uuid(), text: "", category: categoryName },
      ],
    });
  }

  function onRemoveItem(itemId: any) {
    const updateItems = cloneDeep(pendingShoppingList.items);
    const index = updateItems.findIndex((i: any) => i.id === itemId);
    updateItems.splice(index, 1);
    onItemsChange(updateItems);
  }

  function onShoppingListDelete() {
    removeShoppingList(shoppingList.shoppingListId);
    navigate(ROUTES.SHOPPING);
  }

  function onNameChange(name: any) {
    setPendingShoppingList({
      ...pendingShoppingList,
      name,
    });
  }

  function onItemUpdate(item: any) {
    const updateItems = cloneDeep(pendingShoppingList.items);
    const index = updateItems.findIndex((i: any) => i.id === item.id);
    updateItems[index] = item;
    setPendingShoppingList({
      ...pendingShoppingList,
      items: updateItems,
    });
  }

  function handleCheck(itemId: any) {
    const updateItems = cloneDeep(pendingShoppingList.items);
    const index = updateItems.findIndex((i: any) => i.id === itemId);
    updateItems[index].checked = !updateItems[index].checked;
    saveShoppingList(
      {
        ...shoppingList,
        items: updateItems,
      },
      false
    );
  }

  function savePendingShoppingList() {
    saveShoppingList(pendingShoppingList);
    navigate(`${location.pathname}?homeList=${isHome ? "true" : "false"}`);
  }

  function discardPendingShoppingList() {
    setCarefullyCancel(true);
  }

  async function handleCopyForAI() {
    setOrganizeMenuAnchor(null);
    const textToCopy = formatShoppingListForAI(pendingShoppingList);
    if (textToCopy) {
      await navigator.clipboard.writeText(textToCopy);
      setShowPasteDialog(true);
    }
  }

  function handlePasteOrganized() {
    if (pasteText.trim()) {
      const categorizedItems = parseCategorizedTextToItems(
        pasteText,
        pendingShoppingList.items
      );
      setPendingShoppingList({
        ...pendingShoppingList,
        items: categorizedItems,
      });
      setShowPasteDialog(false);
      setPasteText("");
    }
  }

  async function handleAutoOrganize() {
    setOrganizeMenuAnchor(null);
    setOrganizing(true);
    setOrganizeError("");

    const result = await categorizeShoppingList(pendingShoppingList);

    setOrganizing(false);

    if (result.success && result.items) {
      // Show preview instead of applying immediately
      setPreviewItems(result.items);
    } else {
      setOrganizeError(result.error || "Failed to organize list");
    }
  }

  function handleApplyPreview() {
    if (previewItems) {
      const updatedList = {
        ...pendingShoppingList,
        items: previewItems,
      };
      setPendingShoppingList(updatedList);
      // Save to Firestore immediately
      saveShoppingList(updatedList);
      setPreviewItems(null);
    }
  }

  function handleCancelPreview() {
    setPreviewItems(null);
  }

  function toggleCategory(category: string) {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(category)) {
      newCollapsed.delete(category);
    } else {
      newCollapsed.add(category);
    }
    setCollapsedCategories(newCollapsed);
  }

  function handleAddCategory() {
    const trimmedName = newCategoryName.trim();
    if (!trimmedName) return;

    // Check if category already exists
    const existingCategories = Array.from(
      new Set(
        pendingShoppingList.items.map(
          (item: any) => item.category || "Uncategorized"
        )
      )
    );

    if (existingCategories.includes(trimmedName)) {
      // Category already exists - just close dialog
      setShowAddCategoryDialog(false);
      setNewCategoryName("");
      return;
    }

    // Add first item to new category
    addItemToCategory(trimmedName);
    setShowAddCategoryDialog(false);
    setNewCategoryName("");
  }

  const uncheckedItems: any = [];
  const checkedItems: any = [];

  if (pendingShoppingList.items && !!pendingShoppingList.items.length) {
    pendingShoppingList.items.forEach((item: any) => {
      if (item.checked) {
        checkedItems.push(item);
      } else {
        uncheckedItems.push(item);
      }
    });
  }

  // Group unchecked items by category
  const groupedItems = groupItemsByCategory(uncheckedItems);
  const hasCategories = Array.from(groupedItems.keys()).some(
    (category) => category !== "Uncategorized"
  );

  // Build index map for dragging (only unchecked items)
  // IMPORTANT: Indices must match the flat unchecked array, not the grouped display order
  const itemToDragIndex = new Map<string, number>();
  uncheckedItems.forEach((item: any, idx: number) => {
    itemToDragIndex.set(item.id, idx);
  });

  // Determine drop position (top or bottom) based on drag direction
  const getDropPosition = (itemIndex: number) => {
    if (draggedIndex === null || dragOverIndex === null) return null;
    if (dragOverIndex !== itemIndex) return null;

    // If dragging down, show indicator at bottom of target
    // If dragging up, show indicator at top of target
    return draggedIndex < dragOverIndex ? "bottom" : "top";
  };

  return (
    <Box
      key={editMode ? "shoppingListEdit" : "shoppingListView"}
      className={classes.root}
    >
      <TitleBar
        title={pendingShoppingList.name}
        editMode={editMode}
        setTitle={onNameChange}
        onBack={() => navigate("/shopping")}
      />
      <Fade in timeout={DEFAULT_FADE_ANIMATION}>
        <Box>
          {editMode ? (
            <Box className={classes.buttons}>
              <IntentButton
                className={classes.button}
                disabled={!changesMade}
                onClick={() => savePendingShoppingList()}
                variant="contained"
                intent={Intent.PRIMARY}
                startIcon={<SaveIcon />}
              >
                Save
              </IntentButton>
              <IntentButton
                className={classes.button}
                onClick={() => discardPendingShoppingList()}
                variant="contained"
                intent={Intent.TERTIARY}
                startIcon={<CancelIcon />}
              >
                Cancel
              </IntentButton>
            </Box>
          ) : (
            <Box className={classes.buttons}>
              <IntentButton
                className={classes.button}
                onClick={() =>
                  navigate(
                    `${location.pathname}?edit=true&homeList=${query.homeList}`
                  )
                }
                variant="contained"
                intent={Intent.PRIMARY}
                startIcon={<EditIcon fontSize="small" />}
              >
                Edit
              </IntentButton>
              <IntentButton
                className={classes.button}
                onClick={(e) => setOrganizeMenuAnchor(e.currentTarget)}
                variant="contained"
                intent={Intent.PRIMARY}
                startIcon={<CategoryIcon />}
                disabled={uncheckedItems.length === 0}
              >
                Organize
              </IntentButton>
              <IntentButton
                className={classes.button}
                onClick={() => setShowDeleteDialog(true)}
                variant="contained"
                intent={Intent.ERROR}
                startIcon={<DeleteIcon />}
              >
                Delete
              </IntentButton>
            </Box>
          )}
          <Box>
            <List className={classes.list}>
              <Divider />
              {hasCategories ? (
                // Render items grouped by category
                Array.from(groupedItems.entries()).map(([category, items]) => (
                  <Box key={category}>
                    <Box
                      className={classes.categoryHeader}
                      onClick={() => toggleCategory(category)}
                    >
                      <Box className={classes.categoryTitle}>
                        <Typography variant="subtitle1">{category}</Typography>
                        <Typography variant="caption">
                          ({items.length})
                        </Typography>
                      </Box>
                      {collapsedCategories.has(category) ? (
                        <ExpandMoreIcon />
                      ) : (
                        <ExpandLessIcon />
                      )}
                    </Box>
                    <Collapse in={!collapsedCategories.has(category)}>
                      <TransitionGroup>
                        {items.map((item, index) => (
                          <CSSTransition
                            key={item.id}
                            timeout={500}
                            classNames={{
                              enter: classes.enter,
                              enterActive: classes.enterActive,
                              exit: classes.exit,
                              exitActive: classes.exitActive,
                            }}
                          >
                            <ShoppingListItem
                              autoFocus={draggedIndex === null}
                              editMode={editMode}
                              key={item.id}
                              item={item}
                              setItem={onItemUpdate}
                              removeItem={() => onRemoveItem(item.id)}
                              handleCheck={() => handleCheck(item.id)}
                              addItem={
                                index === items.length - 1
                                  ? () =>
                                      addItemToCategory(
                                        category === "Uncategorized"
                                          ? undefined
                                          : category
                                      )
                                  : null
                              }
                              isDraggable={editMode}
                              dragIndex={itemToDragIndex.get(item.id)}
                              isDragged={
                                editMode &&
                                draggedIndex === itemToDragIndex.get(item.id)
                              }
                              isDropTarget={
                                editMode &&
                                dragOverIndex === itemToDragIndex.get(item.id)
                              }
                              dropPosition={
                                editMode
                                  ? getDropPosition(
                                      itemToDragIndex.get(item.id)!
                                    )
                                  : null
                              }
                              dragHandlers={
                                editMode
                                  ? {
                                      handleDragStart:
                                        handleDragStartWithScroll(
                                          itemToDragIndex.get(item.id)!
                                        ),
                                      handleDragOver: handleDragOver(
                                        itemToDragIndex.get(item.id)!
                                      ),
                                      handleDrop: handleDrop(
                                        itemToDragIndex.get(item.id)!
                                      ),
                                      handleDragEnd: handleDragEndWithScroll,
                                      handleTouchStart:
                                        handleTouchStartWithScroll(
                                          itemToDragIndex.get(item.id)!
                                        ),
                                    }
                                  : undefined
                              }
                            />
                          </CSSTransition>
                        ))}
                      </TransitionGroup>

                      {editMode && (
                        <ListItem
                          key={`add-item-${category}`}
                          onClick={() =>
                            addItemToCategory(
                              category === "Uncategorized"
                                ? undefined
                                : category
                            )
                          }
                        >
                          <AddIcon className={classes.addIcon} />
                          <ListItemText
                            className={classes.addText}
                            primary="Add Item"
                          />
                        </ListItem>
                      )}
                    </Collapse>
                  </Box>
                ))
              ) : (
                // Render flat list when no categories
                <TransitionGroup>
                  {uncheckedItems.map((item, index) => (
                    <CSSTransition
                      key={item.id}
                      timeout={500}
                      classNames={{
                        enter: classes.enter,
                        enterActive: classes.enterActive,
                        exit: classes.exit,
                        exitActive: classes.exitActive,
                      }}
                    >
                      <ShoppingListItem
                        autoFocus={draggedIndex === null}
                        editMode={editMode}
                        key={item.id}
                        item={item}
                        setItem={onItemUpdate}
                        removeItem={() => onRemoveItem(item.id)}
                        handleCheck={() => handleCheck(item.id)}
                        addItem={
                          index === uncheckedItems.length - 1 ? addItem : null
                        }
                        isDraggable={editMode}
                        dragIndex={itemToDragIndex.get(item.id)}
                        isDragged={
                          editMode &&
                          draggedIndex === itemToDragIndex.get(item.id)
                        }
                        isDropTarget={
                          editMode &&
                          dragOverIndex === itemToDragIndex.get(item.id)
                        }
                        dropPosition={
                          editMode
                            ? getDropPosition(itemToDragIndex.get(item.id)!)
                            : null
                        }
                        dragHandlers={
                          editMode
                            ? {
                                handleDragStart: handleDragStartWithScroll(
                                  itemToDragIndex.get(item.id)!
                                ),
                                handleDragOver: handleDragOver(
                                  itemToDragIndex.get(item.id)!
                                ),
                                handleDrop: handleDrop(
                                  itemToDragIndex.get(item.id)!
                                ),
                                handleDragEnd: handleDragEndWithScroll,
                                handleTouchStart: handleTouchStartWithScroll(
                                  itemToDragIndex.get(item.id)!
                                ),
                              }
                            : undefined
                        }
                      />
                    </CSSTransition>
                  ))}
                </TransitionGroup>
              )}
              {editMode && !hasCategories && (
                <ListItem key="add-item" onClick={addItem}>
                  <AddIcon className={classes.addIcon} />
                  <ListItemText
                    className={classes.addText}
                    primary="Add Item"
                  />
                </ListItem>
              )}
              {editMode && hasCategories && (
                <ListItem
                  key="add-category"
                  onClick={() => setShowAddCategoryDialog(true)}
                >
                  <AddIcon className={classes.addIcon} />
                  <ListItemText
                    className={classes.addText}
                    primary="Add Category"
                  />
                </ListItem>
              )}
              <Divider />
              <Typography
                key="ticked-items-text"
                className={classes.checkedItemsText}
                variant="body1"
              >
                {`${checkedItems.length} Ticked item${
                  checkedItems.length !== 1 ? "s" : ""
                }`}
              </Typography>
              <TransitionGroup>
                {checkedItems.map((item) => (
                  <CSSTransition
                    key={item.id}
                    timeout={500}
                    classNames={{
                      enter: classes.enter,
                      enterActive: classes.enterActive,
                      exit: classes.exit,
                      exitActive: classes.exitActive,
                    }}
                  >
                    <ShoppingListItem
                      editMode={editMode}
                      key={item.id}
                      item={item}
                      setItem={onItemUpdate}
                      removeItem={() => onRemoveItem(item.id)}
                      handleCheck={() => handleCheck(item.id)}
                    />
                  </CSSTransition>
                ))}
              </TransitionGroup>
            </List>
          </Box>
          <ConfirmDeleteDialog
            open={showDeleteDialog}
            handleClose={() => setShowDeleteDialog(false)}
            item={shoppingList}
            onDelete={onShoppingListDelete}
            idProp="shoppingListId"
            itemType="Shopping List"
          />

          {/* Organize Menu */}
          <Menu
            anchorEl={organizeMenuAnchor}
            open={Boolean(organizeMenuAnchor)}
            onClose={() => setOrganizeMenuAnchor(null)}
          >
            <MenuItem onClick={handleCopyForAI}>
              <ContentCopyIcon sx={{ mr: 1 }} />
              Copy for ChatGPT
            </MenuItem>
            <MenuItem onClick={handleAutoOrganize} disabled={organizing}>
              <AutoAwesomeIcon sx={{ mr: 1 }} />
              Auto-Organize with AI
            </MenuItem>
          </Menu>

          {/* Paste Dialog */}
          <Dialog
            open={showPasteDialog}
            onClose={() => setShowPasteDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Paste Organized List</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                The shopping list has been copied to your clipboard with an AI
                prompt. Paste it into ChatGPT or another AI assistant, then
                paste the organized result back here.
              </Typography>
              <TextField
                multiline
                rows={10}
                fullWidth
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder="Paste the organized list here..."
                variant="outlined"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPasteDialog(false)}>Cancel</Button>
              <Button
                onClick={handlePasteOrganized}
                variant="contained"
                disabled={!pasteText.trim()}
              >
                Apply Categories
              </Button>
            </DialogActions>
          </Dialog>

          {/* Preview Dialog */}
          <Dialog
            open={!!previewItems}
            onClose={handleCancelPreview}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Preview Organized List</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Review the AI-organized categories below. Click "Apply" to save
                these changes or "Cancel" to keep your current list.
              </Typography>
              {previewItems &&
                (() => {
                  const previewUnchecked = previewItems.filter(
                    (item: any) => !item.checked
                  );
                  const previewGrouped = groupItemsByCategory(previewUnchecked);
                  return (
                    <List sx={{ maxHeight: "400px", overflow: "auto" }}>
                      {Array.from(previewGrouped.entries()).map(
                        ([category, items]) => (
                          <Box key={category} sx={{ mb: 1 }}>
                            <Box
                              sx={{
                                padding: "8px 16px",
                                backgroundColor: "primary.light",
                                color: "primary.contrastText",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CategoryIcon fontSize="small" />
                              <Typography variant="subtitle2">
                                {category}
                              </Typography>
                              <Typography variant="caption">
                                ({items.length})
                              </Typography>
                            </Box>
                            {items.map((item: any) => (
                              <ListItem key={item.id} sx={{ py: 0.5 }}>
                                <ListItemText primary={item.text} />
                              </ListItem>
                            ))}
                          </Box>
                        )
                      )}
                    </List>
                  );
                })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelPreview}>Cancel</Button>
              <Button
                onClick={handleApplyPreview}
                variant="contained"
                color="primary"
              >
                Apply
              </Button>
            </DialogActions>
          </Dialog>

          {/* Loading Dialog */}
          <Dialog open={organizing} maxWidth="xs" fullWidth>
            <DialogContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 3,
                }}
              >
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Organizing with AI...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This may take a few seconds
                </Typography>
              </Box>
            </DialogContent>
          </Dialog>

          {/* Error Alert */}
          <Collapse in={!!organizeError}>
            <Alert
              severity="error"
              onClose={() => setOrganizeError("")}
              sx={{ m: 2 }}
            >
              {organizeError}
            </Alert>
          </Collapse>

          {/* Add Category Dialog */}
          <Dialog
            open={showAddCategoryDialog}
            onClose={() => {
              setShowAddCategoryDialog(false);
              setNewCategoryName("");
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Add New Category</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Category Name"
                type="text"
                fullWidth
                variant="outlined"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddCategory();
                  }
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowAddCategoryDialog(false);
                  setNewCategoryName("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddCategory}
                variant="contained"
                color="primary"
                disabled={!newCategoryName.trim()}
              >
                Add
              </Button>
            </DialogActions>
          </Dialog>

          {/* Mobile drag ghost */}
          {editMode && ghostPosition && draggedIndex !== null && (
            <Card
              className={classes.dragGhost}
              style={{
                left: ghostPosition.x,
                top: ghostPosition.y - 30,
                width: "300px",
              }}
            >
              <CardContent>
                <Typography>
                  {uncheckedItems[draggedIndex]?.text || "Moving item..."}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* TODO: React Router v6 removed Prompt component. Need to implement useBlocker hook or similar for navigation blocking */}
          {/* <Prompt
            message={(loc: any, action: any) =>
              editMode &&
              changesMade &&
              (action === "POP" ||
                carefullyCancel ||
                location.pathname !== loc.pathname)
                ? "Are you sure you want to leave this page? You have unsaved changes"
                : true
            }
          /> */}
          <Box id="bottom-shim" style={{ height: "90px" }} />
        </Box>
      </Fade>
    </Box>
  );
}
